import React, { useState } from "react";
import LoaderBackdrop from "../../Components/Loader/LoaderBackdrop";
import { motion } from "framer-motion";
import configServ from "../../services/config";
import { toast } from "react-hot-toast";
import {
  Grid,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Stack,
  IconButton,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import CancelIcon from "@mui/icons-material/Cancel";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const EmailConfigurationCard = ({
  isEdit,
  setIsEdit,
  editData,
  isCreate,
  setIsCreate,
  fetchEmaiConfiguration,
}) => {
  //#region
  const { company_id } = useSelector((state) => state.GlobalVariables);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const [formData, setFormData] = useState({
    mail_mailer: "smtp",
    mail_host: "smtp.gmail.com",
    mail_port: 587,
    mail_username: "",
    mail_password: "",
    mail_encryption: "tls",
    mail_from_address: "",
    mail_from_name: "Your App Name",
    status: false,
    company_id: company_id,
  });

  const validateForm = () => {
    const requiredFields = [
      "mail_username",
      "mail_password",
      "mail_from_address",
      "mail_from_name",
      "mail_mailer",
      "mail_host",
      "mail_port",
      "mail_encryption",
    ];
    for (let field of requiredFields) {
      if (!formData[field]) {
        toast.error(`${field.replace(/_/g, " ")} is required`);
        return false;
      }
    }
    return true;
  };

  const createEmailConfiguration = async () => {
    if (!validateForm()) return;
    setOpenBackdrop(true);
    try {
      const res = await configServ.emailCreate(formData);
      if (res.status === 200) {
        toast.success("Email Configuration Created Successfully!");
        fetchEmaiConfiguration();
        handleCut(); // Reset the form after successful creation
      }
      setIsCreate(false);
    } catch (e) {
      toast.error(
        e?.response?.data?.message || "Failed to create configuration!"
      );
    } finally {
      setOpenBackdrop(false);
    }
  };

  const updateEmailConfiguration = async () => {
    if (!validateForm()) return;

    setOpenBackdrop(true);
    try {
      const res = await configServ.updateEmailConfigure(formData);
      if (res.status === 200) {
        toast.success("Email Configuration Updated Successfully!");
        setIsEdit(false);
        fetchEmaiConfiguration();
        handleCut();
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message || "Failed to update configuration!"
      );
    } finally {
      setOpenBackdrop(false);
    }
  };
  useEffect(() => {
    setOpenBackdrop(true);

    if (isEdit && editData) {
      setFormData({
        mail_mailer: editData.mail_mailer || "smtp",
        mail_host: editData.mail_host || "smtp.gmail.com",
        mail_port: editData.mail_port || 587,
        mail_username: editData.mail_username || "",
        mail_password: editData.mail_password || "",
        mail_encryption: editData.mail_encryption || "tls",
        mail_from_address: editData.mail_from_address || "",
        mail_from_name: editData.mail_from_name || "Your App Name",
        status: editData.status ?? false,
        company_id: editData.company_id || company_id,
        id: editData.id || "",
      });
    }

    setOpenBackdrop(false);
  }, [isEdit, editData, company_id]);

  // Reset the form
  const handleReset = () => {
    setFormData({
      mail_mailer: "",
      mail_host: "",
      mail_port: "",
      mail_username: "",
      mail_password: "",
      mail_encryption: "",
      mail_from_address: "",
      mail_from_name: "",
      status: false,
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      company_id: prev.company_id,
    }));
  };

  const handleCut = () => {
    handleReset();
    setIsCreate(!isCreate);
    setIsEdit(false);
  };
  //#endregion
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: -20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -20 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={{
        width: "100%",
        backgroundColor: "#e3f3fc",
        padding: "20px",
        borderRadius: "10px",
        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
      }}
    >
      <LoaderBackdrop open={openBackdrop} />

      <Grid container spacing={2} sx={{ width: "100%", marginBottom: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            name="mail_username"
            label="Email Username"
            variant="outlined"
            value={formData.mail_username}
            onChange={handleChange}
            fullWidth
            size="small"
            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="mail_password"
            label="Email Password"
            type={showPassword ? "text" : "password"}
            InputProps={{
              endAdornment: (
                <IconButton onClick={togglePasswordVisibility} edge="end">
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              ),
            }}
            variant="outlined"
            value={formData.mail_password}
            onChange={handleChange}
            fullWidth
            size="small"
            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="mail_from_address"
            label="From Email"
            variant="outlined"
            value={formData.mail_from_address}
            onChange={handleChange}
            fullWidth
            size="small"
            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="mail_from_name"
            label="From Name"
            variant="outlined"
            value={formData.mail_from_name}
            onChange={handleChange}
            fullWidth
            size="small"
            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="mail_mailer"
            label="Mailer Name"
            variant="outlined"
            value={formData.mail_mailer}
            onChange={handleChange}
            fullWidth
            size="small"
            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="mail_host"
            label="Host Name"
            variant="outlined"
            value={formData.mail_host}
            onChange={handleChange}
            fullWidth
            size="small"
            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="mail_port"
            label="Port Name"
            variant="outlined"
            type="number"
            value={formData.mail_port}
            onChange={handleChange}
            fullWidth
            size="small"
            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="mail_encryption"
            label="Mail Encryption"
            variant="outlined"
            // type="number"
            value={formData.mail_encryption}
            onChange={handleChange}
            fullWidth
            size="small"
            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                name="status"
                checked={formData.status}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, status: e.target.checked }))
                }
              />
            }
            label="Active"
            sx={{ color: "#555" }}
          />
        </Grid>
      </Grid>

      {/* Buttons */}
      <Stack
        direction="row"
        spacing={2}
        sx={{
          width: "100%",
          justifyContent: "flex-end",
          marginTop: 2,
        }}
      >
        {isEdit ? (
          <IconButton
            onClick={handleCut}
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1,
              backgroundColor: "red",
              color: "white",
              "&:hover": {
                backgroundColor: "darkred",
              },
              boxShadow: 2,
              ml: 1,
              transition: "all 0.3s ease",
            }}
            title="cut"
          >
            <CancelIcon sx={{ fontSize: 18 }} />
          </IconButton>
        ) : (
          <IconButton
            onClick={handleReset}
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1,
              backgroundColor: "secondary.main",
              color: "white",
              "&:hover": {
                backgroundColor: "secondary.dark",
              },
              ml: 1,
            }}
            title="Reset"
          >
            <RotateLeftIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={isEdit ? updateEmailConfiguration : createEmailConfiguration}
          sx={{
            borderRadius: 2,
            px: 2,
            py: 1,
            backgroundColor: isEdit ? "success.main" : "primary.main",
            color: "white",
            "&:hover": {
              backgroundColor: isEdit ? "success.dark" : "primary.dark",
            },
          }}
          title={isEdit ? "Update" : "Save"}
        >
          <SaveIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Stack>
    </motion.div>
  );
};

export default EmailConfigurationCard;
